import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import {
	impactInterface,
	SERVICE_TYPES,
	SERVICE_TYPES_ENUM,
	severityInterface,
	solutionAreaInterface,
	urgencyInterface,
} from 'src/app/util/interfaces/requestClassification';
import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root',
})
export class RequestClassificationReverseService {
	getClassificationReverseUrl = environment.baseApiUrl + 'request/classification/reverse/{classificationStep}';
	getImpactUrl = environment.baseApiUrl + 'register/impactMatrix/impact';
	getUrgencyUrl = environment.baseApiUrl + 'register/impactMatrix/urgency';
	getPriorityUrl = environment.baseApiUrl + 'register/impactMatrix/priority';

	headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	previousStep = {
		serviceType: 'idSolutionArea',
		scope: 'idServiceType',
		category: 'idScope',
		topic: 'idCategory',
	};

	constructor(
		private http: HttpClient,
		private AuthLoginService: AuthLoginService
	) {}

	getSolutionAreas(
		customerList: string,
		onMe: boolean,
		allCustomer: boolean,
		definedUrgency: boolean,
		targetServiceTypes: SERVICE_TYPES[] = ['CHANGE', 'PROBLEM', 'INCIDENT', 'COMPLAINT', 'SERVICE_REQUEST'],
		idTargetCi?: string //if you want to find classifications that bond to the ci ty eof the target ci
	): Observable<solutionAreaInterface[]> {
		const getClassificationReverseUrl = this.getClassificationReverseUrl.replace('{classificationStep}', 'solutionAreas');

		const serviceTypesId = targetServiceTypes.map((serviceType) => SERVICE_TYPES_ENUM[serviceType]);
		const parameters = {
			customerList: customerList,
			onMe: onMe,
			allCustomer: allCustomer,
			definedUrgency: definedUrgency,
			targetServiceTypes: serviceTypesId.join(','),
		};
		if (idTargetCi != undefined) parameters['idTargetCi'] = idTargetCi;

		return this.http.get<solutionAreaInterface[]>(getClassificationReverseUrl, { headers: this.headers, params: parameters }).pipe(
			map((solutionAreaRes: solutionAreaInterface[]) => {
				const newSolutionAreaArray: solutionAreaInterface[] = [];
				for (const solutionArea of solutionAreaRes) {
					const newSolutionArea: solutionAreaInterface = {
						id: solutionArea.id,
						name: solutionArea.name,
						label: solutionArea.name,
					};
					newSolutionAreaArray.push(newSolutionArea);
				}

				return newSolutionAreaArray;
			})
		);
	}

	getClassification(
		customerList: string,
		classificationStep: string,
		idParameter: string,
		idSolutionArea: string,
		definedUrgency: boolean,
		serviceType?: string,
		targetServiceTypes: SERVICE_TYPES[] = ['CHANGE', 'PROBLEM', 'INCIDENT', 'COMPLAINT', 'SERVICE_REQUEST'],
		idTargetCi?: string
	): Observable<any[]> {
		const getClassificationReverseUrl = this.getClassificationReverseUrl.replace('{classificationStep}', classificationStep);
		const parameters = {};
		parameters['customerList'] = customerList;
		parameters[this.previousStep[classificationStep]] = idParameter;
		parameters['idSolutionArea'] = idSolutionArea;
		if (classificationStep == 'category') parameters['idServiceType'] = serviceType;
		if (targetServiceTypes && targetServiceTypes.length > 0)
			parameters['targetServiceTypes'] = targetServiceTypes.map((serviceType) => SERVICE_TYPES_ENUM[serviceType]).join(',');
		parameters['definedUrgency'] = definedUrgency;
		if (idTargetCi != undefined) parameters['idTargetCi'] = idTargetCi;

		return this.http.get<any[]>(getClassificationReverseUrl, { headers: this.headers, params: parameters }).pipe(
			map((classificationRes: any[]) => {
				const newClassificationArray: any[] = [];
				for (const classification of classificationRes) {
					const newClassification = {
						id: classification.id,
						name: classification.name,
						label: classification.label ? classification.label : classification.name,
						allowedCiTypes: classification.allowedCiTypes,
					};

					newClassificationArray.push(newClassification);
				}

				return newClassificationArray;
			})
		);
	}

	getImpact(getDefault: boolean, idSolutionArea?: string): Observable<impactInterface[]> {
		return this.http
			.get<
				any[]
			>(this.getImpactUrl, { headers: this.headers, params: { default: getDefault, idSolutionArea: idSolutionArea ? idSolutionArea : '' } })
			.pipe(
				map((impactRes) => {
					const newImpactReturn: impactInterface[] = [];
					for (const impact of impactRes) {
						const newImpact: impactInterface = {
							id: impact.id,
							label: impact.label,
							name: impact.name,
							impact: impact.impact,
							default: impact.default == 1,
							default_classification: impact.def_impact === 1 ? true : false,
						};
						newImpactReturn.push(newImpact);
					}
					return newImpactReturn;
				})
			);
	}

	getUrgency(getDefault: boolean, idTopic?: string, idSolutionArea?: string): Observable<urgencyInterface[]> {
		return this.http
			.get<
				any[]
			>(this.getUrgencyUrl, { headers: this.headers, params: { default: getDefault, idTopic: idTopic ? idTopic : '', idSolutionArea: idSolutionArea ? idSolutionArea : '' } })
			.pipe(
				map((urgencyRes) => {
					const newUrgencyReturn: urgencyInterface[] = [];
					for (const urgency of urgencyRes) {
						const newUrgency: urgencyInterface = {
							id: urgency.id,
							label: urgency.label,
							name: urgency.name,
							urgency: urgency.urgency,
							default: urgency.default == 1,
							default_classification: urgency.def_urgency === 1 ? true : false,
						};
						newUrgencyReturn.push(newUrgency);
					}
					return newUrgencyReturn;
				})
			);
	}

	getSeverity(impact: string, urgency: string, getDefault: boolean, customerList: string): Observable<severityInterface[]> {
		return this.http
			.get<
				any[]
			>(this.getPriorityUrl, { headers: this.headers, params: { impact: impact, urgency: urgency, default: getDefault, customerList: customerList } })
			.pipe(
				map((priorityRes) => {
					const newPriorityReturn: severityInterface[] = [];
					for (const priority of priorityRes) {
						const newPriority: severityInterface = {
							id: priority.id,
							label: priority.severity,
							name: priority.severity,
							severity: priority.severity,
						};
						newPriorityReturn.push(newPriority);
					}
					return newPriorityReturn;
				})
			);
	}
}
